import { Container } from '@/components/shared/Container'
import Image from 'next/image'
import { trackVideoEvent } from '@/lib/gtm/gtmV2'
import { useRef, useState } from 'react'
import { StructuredText } from 'react-datocms'
import styled from 'styled-components'
import { VideoHeroBlockRecord } from 'types'
import { ButtonBlock } from './ButtonBlock'
import { TextWithIconBlock } from './TextWithIcon'

const StyledVideo = styled.section`
  padding: 192px 0 120px;
  @media screen and (max-width: ${(p) => p.theme.breakpoints.desktop}) {
    padding: 144px 0 80px;
  }
  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    padding: 120px 0 0;
  }
`

const StyledVideoRow = styled.div`
  display: flex;
  align-items: center;

  @media screen and (max-width: ${(p) => p.theme.breakpoints.desktop}) {
    flex-direction: column;
  }
`

const StyledVideoText = styled.div`
  max-width: 320px;
  width: 100%;

  h2 {
    font-size: ${(p) => p.theme.v2.typography.display.xs.fontSize};
    line-height: ${(p) => p.theme.v2.typography.display.xs.lineHeight};
    font-family: ${(p) => p.theme.v2.font.nantes};
    color: ${(props) => props.theme.v2.onSurface.text};
    margin: 0;

    em {
      font-family: ${(p) => p.theme.v2.font.nantesItalic};
    }
  }

  p {
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueLight};
    color: ${(props) => props.theme.v2.onSurface.subduedText};
    ${(p) => p.theme.v2.typography.body.s};
    margin: 16px 0 32px;
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoints.desktop}) {
    max-width: 568px;
    text-align: center;

    h2 {
      font-size: ${(p) => p.theme.v2.typography.display.l.fontSize};
      line-height: ${(p) => p.theme.v2.typography.display.l.lineHeight};
      font-family: ${(p) => p.theme.v2.font.nantes};

      em {
        font-family: ${(p) => p.theme.v2.font.nantesItalic};
      }
    }

    p {
      &.without-button {
        padding-top: 30px;
        margin-bottom: -30px;
      }
    }
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    padding: 0 20px;

    h2 {
      font-size: ${(p) => p.theme.v2.typography.body.xl.fontSize};
      line-height: ${(p) => p.theme.v2.typography.body.xl.lineHeight};
      max-width: 250px;
      margin: 0 auto;
    }
  }
`

const StyledVideoRight = styled.div`
  flex: 1;
  margin-left: 32px;
  @media screen and (max-width: ${(p) => p.theme.breakpoints.desktop}) {
    margin: 48px 0 0 0;
  }
`

const StyledVideoWrap = styled.div`
  border-radius: 8px;
  overflow: hidden;
  position: relative;

  video {
    display: block;
    height: auto;
    visibility: visible;
    transition: opacity 0.3s ease-in;
    max-width: 100%;

    &#loop::-webkit-media-controls {
      display: none;
    }

    &#full {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 20;
    }

    &.hidden {
      opacity: 0;
      visibility: hidden;
    }
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    border-radius: 0;
  }
`

const StyledPlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 10;
  transform: translate(-50%, calc(-50% + 10px));
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  p {
    text-shadow: 1px 1px 4px rgb(0 0 0 / 60%);
    ${(p) => p.theme.v2.typography.label.l};
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueSemiBold};
    color: white;
    margin: 5px 0 0 0;
  }
`

const StyledPlayIcon = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  width: 60px;
  height: 60px;
  margin-bottom: 0;
  border-width: 2px;
  border: 3px solid #fff;
  border-radius: 45px;
  box-shadow: 0 0 15px 0 rgb(0 0 0 / 10%);
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    transform: translateX(2px);
  }
`

const renderBlock = ({ record }) => {
  switch (record.__typename) {
    case 'ButtonRecord':
      return <ButtonBlock record={record} />
    case 'TextWithIconRecord':
      return <TextWithIconBlock record={record} />
    default:
      return null
  }
}

export type VideoHeroProps = {
  record: VideoHeroBlockRecord
  videoTitle: string
}

export const VideoHero: React.FC<VideoHeroProps> = ({ record, videoTitle }) => {
  const [playVideo, setPlayVideo] = useState(false)
  const fullVideo = useRef<HTMLVideoElement>(null)
  const { videoUrlLoop, videoUrlFull, placeholderImage, videoEmbeddedFull, videoEmbeddedLoop, content } = record
  const fullVideoMuxUrl = videoEmbeddedFull?.video?.mp4Med
  const loopMp4Url = videoEmbeddedLoop?.video?.mp4Med

  const playButtonClickHandler = () => {
    fullVideo.current?.play()
    setPlayVideo((playVideo) => !playVideo)
    trackVideoEvent('cms', 'Play', videoTitle, fullVideo.current?.currentTime)
  }

  const videoPauseHandler = () => {
    trackVideoEvent('cms', 'Pause', videoTitle, fullVideo.current?.currentTime)
  }

  const videoCompleteHandler = () => {
    trackVideoEvent('cms', 'Complete', videoTitle, fullVideo.current?.currentTime)
  }

  return (
    <StyledVideo className="video-hero">
      <Container $padding="no-mobile">
        <StyledVideoRow>
          <StyledVideoText>
            <StructuredText data={content} renderBlock={renderBlock} />
          </StyledVideoText>

          <StyledVideoRight>
            <StyledVideoWrap>
              <video
                id="loop"
                preload="none"
                playsInline
                width="1280"
                autoPlay
                muted
                loop
                poster={placeholderImage.url}
              >
                <source src={loopMp4Url ? loopMp4Url : videoUrlLoop}></source>
              </video>

              <video
                ref={fullVideo}
                id="full"
                playsInline
                width="1280"
                controls
                className={playVideo ? '' : 'hidden'}
                onPause={videoPauseHandler}
                onEnded={videoCompleteHandler}
              >
                <source src={fullVideoMuxUrl ? fullVideoMuxUrl : videoUrlFull}></source>
              </video>

              {!playVideo && (
                <StyledPlayButton onClick={playButtonClickHandler}>
                  <StyledPlayIcon>
                    <Image src="/assets/img/own-zoe/play.svg" alt="" width={23} height={26} />
                  </StyledPlayIcon>

                  <p>Press to play</p>
                </StyledPlayButton>
              )}
            </StyledVideoWrap>
          </StyledVideoRight>
        </StyledVideoRow>
      </Container>
    </StyledVideo>
  )
}
